/**
 * Collectibles, and the builder that makes many at once.
 *
 * Not every file uploaded is a collectible, so creating one is explicit. What
 * keeps that from being tedious is the builder: pick files, describe the
 * pattern once, SEE WHAT IT WOULD DO, then commit.
 *
 * That preview is the part worth having. A build is all-or-nothing, so the
 * only way to find out what it would produce is to ask — and their
 * equivalent, which does not exist, is called "the main throughput bottleneck
 * before launch" in their own punchlist.
 */
import { useMemo, useState, type ReactNode } from 'react';
import type { Asset, Item, ItemBuildPreview } from '@tomo/contracts';
import { Shell } from '../components/Shell.jsx';
import { Badge, Button, Empty, Failed, Spinner } from '../components/primitives.jsx';
import { Check, Field, Text } from '../components/form.jsx';
import { ApiError } from '../lib/api.js';
import { assets as assetsApi, items } from '../lib/tomo.js';
import { useAsync } from '../useAsync.js';
import { useSession } from '../Session.jsx';

export function Items({ channelId }: { channelId: string }): ReactNode {
  const session = useSession();
  const canEdit = session.channels
    .find((c) => c.id === channelId)?.scopes.includes('rewards:write') ?? false;

  const [loaded, retry] = useAsync(
    async (signal) => ({
      items: await items.list(channelId, signal),
      assets: await assetsApi.list(channelId, signal),
    }),
    [channelId]
  );
  const [building, setBuilding] = useState(false);

  return (
    <Shell channelId={channelId}>
      <div className="flex items-start justify-between gap-6">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Collectibles</h1>
          <p className="mt-1 text-sm text-ink-400">
            What a person says they own. Built from your files — one file can back
            several, and one collectible can have different art per rarity.
          </p>
        </div>
        {canEdit && loaded.state === 'ready' && !building ? (
          <Button onClick={() => { setBuilding(true); }}>Build from files</Button>
        ) : null}
      </div>

      {loaded.state === 'loading' ? <Spinner label="Loading…" /> : null}
      {loaded.state === 'failed' ? (
        <div className="mt-8">
          <Failed message="Could not load this channel." detail={loaded.error.message} onRetry={retry} />
        </div>
      ) : null}

      {loaded.state === 'ready' ? (
        building ? (
          <Builder
            channelId={channelId}
            assets={loaded.value.assets.filter((a) => a.status === 'active')}
            onDone={() => { setBuilding(false); retry(); }}
            onCancel={() => { setBuilding(false); }}
          />
        ) : (
          <List items={loaded.value.items} assets={loaded.value.assets} canBuild={canEdit}
                onBuild={() => { setBuilding(true); }} />
        )
      ) : null}
    </Shell>
  );
}

function List(
  { items: list, assets, canBuild, onBuild }:
  { items: Item[]; assets: Asset[]; canBuild: boolean; onBuild: () => void }
): ReactNode {
  const art = useMemo(() => new Map(assets.map((a) => [a.id, a])), [assets]);

  if (list.length === 0) {
    return (
      <div className="mt-8">
        <Empty
          title="No collectibles yet."
          hint={assets.length === 0
            ? 'Upload some files first — collectibles are built from them.'
            : 'Build them from the files you have uploaded.'}
          action={canBuild && assets.length > 0
            ? <Button onClick={onBuild}>Build from files</Button>
            : undefined}
        />
      </div>
    );
  }

  return (
    <ul className="mt-8 grid gap-4 sm:grid-cols-3 lg:grid-cols-4">
      {list.map((item) => {
        const asset = item.art.default ? art.get(item.art.default) : undefined;
        // Shown, not hidden: the Part Maker's scar was divergence with NO
        // indication, and a re-apply silently overwriting the edit.
        const edited = item.stamp !== null && item.stamp.name !== item.name;
        return (
          <li key={item.id} className="overflow-hidden rounded-lg border border-ink-800">
            <span className="grid aspect-square place-items-center bg-ink-900">
              {asset?.url ? (
                <img src={asset.url} alt={item.name} loading="lazy" className="size-full object-contain" />
              ) : (
                <span className="text-xs text-ink-700">no art</span>
              )}
            </span>
            <span className="block p-3">
              <span className="block truncate text-sm text-ink-50">{item.name}</span>
              {item.external_id ? (
                <span className="block truncate font-mono text-xs text-ink-700">{item.external_id}</span>
              ) : null}
              <span className="mt-1.5 flex flex-wrap gap-1.5">
                {item.status !== 'active' ? <Badge tone="warn">retired</Badge> : null}
                {edited ? <Badge tone="warn">edited</Badge> : null}
                {Object.keys(item.art.by_tier).length > 0 ? (
                  <Badge tone="brand">{Object.keys(item.art.by_tier).length} tier arts</Badge>
                ) : null}
                {item.tags.slice(0, 2).map((t) => <Badge key={t}>#{t}</Badge>)}
              </span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function Builder(
  { channelId, assets, onDone, onCancel }:
  { channelId: string; assets: Asset[]; onDone: () => void; onCancel: () => void }
): ReactNode {
  const [picked, setPicked] = useState<string[]>([]);
  const [namePattern, setNamePattern] = useState('{name}');
  const [externalIdPattern, setExternalIdPattern] = useState('');
  const [tidyNames, setTidyNames] = useState(true);
  const [inheritAssetTags, setInheritAssetTags] = useState(true);
  const [preview, setPreview] = useState<ItemBuildPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const template = {
    name_pattern: namePattern,
    external_id_pattern: externalIdPattern,
    kind: 'tomo:collectible',
    tags: [],
    inherit_asset_tags: inheritAssetTags,
    tidy_names: tidyNames,
  };

  const toggle = (id: string): void => {
    setPreview(null);
    setPicked((all) => (all.includes(id) ? all.filter((x) => x !== id) : [...all, id]));
  };

  const run = (commit: boolean): void => {
    setBusy(true);
    setError(null);
    const action = commit
      ? items.build(channelId, picked, template).then(onDone)
      : items.preview(channelId, picked, template).then(setPreview);
    action.then(
      () => { setBusy(false); },
      (err: unknown) => {
        setBusy(false);
        setError(err instanceof ApiError ? err.message : 'Something went wrong.');
      }
    );
  };

  const problems = preview?.problems ?? [];
  const clean = preview !== null && problems.length === 0;

  return (
    <div className="mt-8 space-y-8">
      <section>
        <h2 className="text-sm font-medium uppercase tracking-wide text-ink-400">
          1 · Pick files {picked.length > 0 ? `· ${picked.length} selected` : null}
        </h2>
        {assets.length === 0 ? (
          <p className="mt-3 text-sm text-ink-400">No files uploaded yet.</p>
        ) : (
          <ul className="mt-3 grid gap-2 sm:grid-cols-4 lg:grid-cols-6">
            {assets.map((asset) => (
              <li key={asset.id}>
                <button
                  type="button"
                  onClick={() => { toggle(asset.id); }}
                  className={`block w-full overflow-hidden rounded-lg border text-left ${
                    picked.includes(asset.id)
                      ? 'border-brand-400 ring-1 ring-brand-400'
                      : 'border-ink-800 hover:border-ink-700'
                  }`}
                >
                  <span className="grid aspect-square place-items-center bg-ink-900">
                    {asset.url ? (
                      <img src={asset.url} alt={asset.name} loading="lazy" className="size-full object-contain" />
                    ) : null}
                  </span>
                  <span className="block truncate p-2 text-xs text-ink-400">{asset.name}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="max-w-lg space-y-4">
        <h2 className="text-sm font-medium uppercase tracking-wide text-ink-400">
          2 · Describe them once
        </h2>
        <Field
          label="Name"
          hint="{name} is the file's name, {index} its position, {slug} a url-safe form."
        >
          <Text value={namePattern} onChange={(v) => { setNamePattern(v); setPreview(null); }} />
        </Field>
        <Field
          label="Your game's id"
          hint="Optional. Must be unique on this channel — it is how you map a collectible back."
        >
          <Text
            value={externalIdPattern}
            onChange={(v) => { setExternalIdPattern(v); setPreview(null); }}
            placeholder="WPN_{slug}"
          />
        </Field>
        <Check
          checked={tidyNames}
          onChange={(v) => { setTidyNames(v); setPreview(null); }}
          label="Tidy filenames — plasma-rifle.png becomes Plasma Rifle"
        />
        <Check
          checked={inheritAssetTags}
          onChange={(v) => { setInheritAssetTags(v); setPreview(null); }}
          label="Carry each file's tags onto its collectible"
        />
      </section>

      <section>
        <h2 className="text-sm font-medium uppercase tracking-wide text-ink-400">
          3 · See what it would do
        </h2>
        <p className="mt-1 text-xs text-ink-400">
          Nothing is created until you say so, and a batch with any problem creates none of it.
        </p>

        {error ? <div className="mt-3 max-w-lg"><Failed message={error} /></div> : null}

        {preview ? (
          <>
            {problems.length > 0 ? (
              <ul className="mt-3 max-w-2xl space-y-1 rounded-lg border border-warn-400/40 bg-warn-400/5 p-3">
                {problems.map((p, i) => (
                  <li key={`${p.asset_id}-${String(i)}`} className="text-xs text-warn-400">
                    {p.message}
                  </li>
                ))}
              </ul>
            ) : null}

            <ul className="mt-3 max-w-2xl divide-y divide-ink-800 rounded-lg border border-ink-800">
              {preview.drafts.map((d) => (
                <li key={d.asset_id} className="flex items-center gap-3 px-4 py-2 text-sm">
                  <span className="flex-1 truncate text-ink-50">{d.name}</span>
                  {d.external_id ? (
                    <span className="truncate font-mono text-xs text-ink-700">{d.external_id}</span>
                  ) : null}
                  {d.tags.slice(0, 3).map((t) => <Badge key={t}>#{t}</Badge>)}
                </li>
              ))}
            </ul>
          </>
        ) : null}

        <div className="mt-6 flex flex-wrap gap-3">
          <Button variant="ghost" disabled={busy || picked.length === 0} onClick={() => { run(false); }}>
            {busy && !clean ? 'Checking…' : 'Preview'}
          </Button>
          <Button disabled={busy || !clean} onClick={() => { run(true); }}>
            {clean ? `Build ${String(preview.drafts.length)}` : 'Build'}
          </Button>
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        </div>
      </section>
    </div>
  );
}
